<template>
  <App :session="session" :office_id="office_id">
    <div class="shop-tabs">
      <router-link class="shop-tabs__link is-active" to="/activation">Comprar</router-link>
      <router-link class="shop-tabs__link" to="/activations" v-if="!office_id">Historial</router-link>
    </div>

    <i class="load" v-if="loading && !invalid"></i>
    <p class="shop-error" v-if="invalid">Usuario no afiliado</p>

    <div class="shop" v-if="!loading && !invalid">
      <!-- PASO 1: Catálogo -->
      <template v-if="step === 1">
        <section class="shop-main">
          <!-- Desktop header (oculto en móvil) -->
          <header class="shop-header shop-header--desktop">
            <div>
              <h2 class="shop-title">Selecciona tus productos</h2>
              <p class="shop-subtitle">
                Explora el catálogo, encuentra tus productos y agrégalos al carrito.
                Antes de continuar, revisa las cantidades, el total y los puntos de tu pedido.
              </p>
            </div>
            <div class="shop-stepper">
              <div class="shop-stepper__item is-active">
                <span class="shop-stepper__num">1</span>
                <span>Productos</span>
              </div>
              <div class="shop-stepper__line"></div>
              <div class="shop-stepper__item">
                <span class="shop-stepper__num">2</span>
                <span>Pago y confirmación</span>
              </div>
            </div>
          </header>

          <!-- Filtros: en móvil van primero como Sifrah -->
          <div class="shop-toolbar">
            <label class="shop-search">
              <i class="fas fa-search"></i>
              <input v-model.trim="search" type="search" placeholder="Búsqueda..." />
            </label>
            <div class="shop-filters">
              <button
                type="button"
                class="shop-chip"
                :class="{ 'is-active': category === 'Todos' }"
                @click="category = 'Todos'"
              >
                Todos
              </button>
              <button
                type="button"
                class="shop-chip"
                v-for="cat in categories"
                :key="cat"
                :class="{ 'is-active': category === cat }"
                @click="category = cat"
              >
                {{ cat }}
              </button>
            </div>
          </div>

          <!-- Barra carrito móvil sticky (como Sifrah) -->
          <div
            class="cart-button-mobile-anchor mobile-cart-anchor"
            :style="mobileCartStuck ? { minHeight: mobileCartBarHeight + 'px' } : null"
          >
            <div ref="mobileCartSentinel" class="cart-button-mobile-sentinel mobile-cart-sentinel" aria-hidden="true"></div>
            <div
              class="cart-button-container-mobile mobile-cart-bar"
              :class="{ 'is-hidden': showCartModal, 'is-stuck': mobileCartStuck }"
              :style="mobileCartStuckStyle"
            >
              <div class="cart-info-left mobile-cart-bar__info">
                <div class="cart-price-info">
                  <span class="total-price mobile-cart-bar__amount">Monto: Bs {{ price.toFixed(2) }}</span>
                  <span class="total-items mobile-cart-bar__points">Puntos: {{ points }} pts</span>
                </div>
              </div>
              <button type="button" class="cart-square-btn mobile-cart-bar__btn" @click="showCartModal = true">
                <i class="fas fa-shopping-cart"></i>
                <span>Ver carrito</span>
              </button>
            </div>
          </div>

          <div class="products-count-indicator shop-count">
            <span>Mostrando {{ filteredProducts.length }} de {{ products.length }} productos</span>
            <span v-if="search || (category && category !== 'Todos')" class="filter-active">
              (filtros activos)
            </span>
          </div>

          <div class="products-catalog-grid shop-grid">
            <article class="product-catalog-card product-card" v-for="product in filteredProducts" :key="product.id || product.name">
              <div class="card-corner"></div>
              <div class="points-badge product-card__badge">
                <i class="fas fa-star"></i>
                {{ product.points || product.price }} pts
              </div>
              <div class="product-image-container product-card__img">
                <img :src="product.img" :alt="product.name" class="product-catalog-img" />
              </div>
              <div class="product-catalog-info product-card__info">
                <h3 class="product-catalog-name product-card__name">{{ product.name }}</h3>
                <div class="product-catalog-info-text product-card__type">{{ product.type || '\u00a0' }}</div>
                <div class="product-catalog-price">
                  Precio Socio: <span class="price-amount product-card__price">Bs {{ product.price }}</span>
                </div>
              </div>
              <div class="product-quantity-controls product-card__controls" v-if="product.total > 0">
                <button type="button" class="qty-control-btn btn-ctrl" @click.stop="less(product)">−</button>
                <span class="quantity-display ctrl-qty">{{ product.total }}</span>
                <button type="button" class="qty-control-btn btn-ctrl" @click.stop="more(product)">+</button>
              </div>
              <div class="add-to-cart-container" v-else>
                <button type="button" class="add-to-cart-btn product-card__btn" @click.stop="addProduct(product)">
                  <i class="fas fa-shopping-cart"></i>
                  Agregar
                </button>
              </div>
            </article>
          </div>
        </section>

        <!-- SIDEBAR CART (DESKTOP ONLY) -->
        <aside class="shop-cart" ref="cartPanel">
          <div class="shop-cart__head">
            <h3>
              <i class="fas fa-shopping-cart"></i>
              Carrito de compras
              <span class="shop-cart__badge" v-if="cartCount">{{ cartCount }}</span>
            </h3>
            <p>Puedes hacer scroll para ver todos tus productos.</p>
          </div>

          <div class="shop-cart__body" v-if="cartItems.length">
            <article class="cart-item" v-for="item in cartItems" :key="item.id || item.name">
              <img :src="item.img" :alt="item.name" />
              <div class="cart-item__info">
                <strong>{{ item.name }}</strong>
                <span>Bs {{ item.price }}</span>
                <small>{{ item.points || item.price }} puntos</small>
              </div>
              <div class="cart-item__actions">
                <div class="cart-item__qty">
                  <button type="button" @click="less(item)">−</button>
                  <span>{{ item.total }}</span>
                  <button type="button" @click="more(item)">+</button>
                </div>
                <button type="button" class="cart-item__remove" @click="removeProduct(item)" title="Quitar">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </article>
          </div>
          <div class="shop-cart__empty" v-else>
            <i class="fas fa-shopping-cart"></i>
            <p>Tu carrito está vacío</p>
            <small>Agrega productos para comenzar.</small>
          </div>

          <div class="shop-cart__summary">
            <h4>Resumen</h4>
            <div class="row"><span>Total productos</span><span>{{ cartCount }} {{ cartCount === 1 ? 'item' : 'items' }}</span></div>
            <div class="row"><span>Subtotal</span><span>Bs {{ subtotal.toFixed(2) }}</span></div>
            <div class="row"><span>Impuestos (18%)</span><span>Bs {{ IGV.toFixed(2) }}</span></div>
            <div class="row row--total"><span>Total</span><span>Bs {{ price.toFixed(2) }}</span></div>
            <div class="row"><span>Puntos totales</span><span>{{ points }}</span></div>
          </div>

          <div class="shop-cart__actions">
            <button type="button" class="btn btn--primary" :disabled="!cartCount" @click="goCheckout">
              <i class="fas fa-shopping-cart"></i>
              Ir a pagar
            </button>
            <button type="button" class="btn btn--ghost" @click="showCartModal = true">
              Ver detalle
            </button>
          </div>
        </aside>

        <!-- MODAL CART (ALL DEVICES) -->
        <div class="cart-overlay" :class="{'is-open': showCartModal}" @click.self="showCartModal = false">
          <aside class="shop-cart shop-cart--modal">
            <div class="shop-cart__head">
              <h3>
                <i class="fas fa-shopping-cart"></i>
                Carrito de compras
                <span class="shop-cart__badge" v-if="cartCount">{{ cartCount }}</span>
              </h3>
              <button type="button" class="shop-cart__close" @click="showCartModal = false">
                <i class="fas fa-times"></i>
              </button>
              <p>Puedes hacer scroll para ver todos tus productos.</p>
              <h4 class="shop-cart__subtitle">Productos en tu carrito</h4>
            </div>

            <div class="shop-cart__body" v-if="cartItems.length">
              <article class="cart-item" v-for="item in cartItems" :key="item.id || item.name">
                <img :src="item.img" :alt="item.name" />
                <div class="cart-item__info">
                  <strong>{{ item.name }}</strong>
                  <span>Bs {{ item.price }}</span>
                  <small>{{ item.points || item.price }} puntos</small>
                </div>
                <div class="cart-item__actions">
                  <div class="cart-item__qty">
                    <button type="button" @click="less(item)">−</button>
                    <span>{{ item.total }}</span>
                    <button type="button" @click="more(item)">+</button>
                  </div>
                  <button type="button" class="cart-item__remove" @click="removeProduct(item)" title="Quitar">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </article>
            </div>
            <div class="shop-cart__empty" v-else>
              <i class="fas fa-shopping-cart"></i>
              <p>Tu carrito está vacío</p>
              <small>Agrega productos para comenzar.</small>
            </div>

            <div class="shop-cart__summary">
              <h4>Resumen</h4>
              <div class="row"><span>Total productos</span><span>{{ cartCount }} {{ cartCount === 1 ? 'item' : 'items' }}</span></div>
              <div class="row"><span>Subtotal</span><span>Bs {{ subtotal.toFixed(2) }}</span></div>
              <div class="row"><span>Impuestos (18%)</span><span>Bs {{ IGV.toFixed(2) }}</span></div>
              <div class="row row--total"><span>Total</span><span>Bs {{ price.toFixed(2) }}</span></div>
              <div class="row"><span>Puntos totales</span><span>{{ points }}</span></div>
            </div>

            <div class="shop-cart__actions">
              <button type="button" class="btn btn--primary" @click="showCartModal = false">
                Añadir más productos
              </button>
              <button type="button" class="btn btn--ghost" :disabled="!cartCount" @click="goCheckout">
                <i class="fas fa-shopping-cart"></i>
                Ir a pagar
              </button>
            </div>
          </aside>
        </div>
      </template>

      <!-- PASO 2: Checkout -->
      <template v-else>
        <section class="shop-main">
          <header class="shop-header">
            <div>
              <h2 class="shop-title">Confirma tu pedido</h2>
              <p class="shop-subtitle">
                Selecciona la oficina, revisa los datos de pago y elige cómo deseas pagar.
                Si realizaste una transferencia, registra la información y adjunta tu comprobante.
              </p>
            </div>
            <div class="shop-stepper">
              <div class="shop-stepper__item is-done">
                <span class="shop-stepper__num"><i class="fas fa-check"></i></span>
                <span>Productos</span>
              </div>
              <div class="shop-stepper__line is-done"></div>
              <div class="shop-stepper__item is-active">
                <span class="shop-stepper__num">2</span>
                <span>Pago y confirmación</span>
              </div>
            </div>
          </header>

          <div class="checkout-block">
            <h3 class="checkout-block__title"><span>1</span> Selecciona la oficina</h3>
            <label class="field">
              <span>Oficina de atención</span>
              <select v-model="office" :disabled="!!office_id">
                <option :value="null" disabled>Selecciona una oficina</option>
                <option v-for="o in offices" :key="o.id || o.name" :value="o">{{ o.name }}</option>
              </select>
            </label>
            <p class="field-hint" v-if="office">{{ office.address }}</p>
          </div>

          <div class="checkout-payinfo" v-if="office">
            <div class="pay-card">
              <h4>Datos para realizar el pago</h4>
              <pre class="pay-card__accounts">{{ office.accounts }}</pre>
              <button type="button" class="btn-copy" @click="copyAccounts" v-if="office.accounts">
                <i class="fas fa-copy"></i> Copiar datos
              </button>
            </div>
            <div class="pay-card pay-card--qr">
              <h4>QR de pago</h4>
              <img
                :src="office.qr_image || office.qr || defaultQr"
                alt="QR de pago"
              />
            </div>
          </div>

          <div class="checkout-block">
            <h3 class="checkout-block__title"><span>2</span> Aplica tu saldo</h3>
            <label class="toggle-row">
              <span>Deseo usar mi saldo disponible</span>
              <input type="checkbox" v-model="check" class="toggle-input" />
              <span class="toggle-ui"></span>
            </label>
            <div class="balance-box" v-if="check && platform_configs">
              <p v-if="platform_configs.show_ballance.aff">
                Saldo bono afiliados: <b>Bs {{ _balance_aff }}</b>
              </p>
              <p>Saldo no disponible: <b>Bs {{ _balance }}</b></p>
              <p v-if="platform_configs.show_ballance.main">
                Saldo disponible: <b>Bs {{ balance }}</b>
              </p>
              <p v-if="remaining > 0">Restan por pagar: <b>Bs {{ remaining.toFixed(2) }}</b></p>
              <p v-else class="ok">Tu saldo cubre el total del pedido.</p>
            </div>
          </div>

          <div class="checkout-block" v-show="!(check && remaining == 0)">
            <h3 class="checkout-block__title"><span>3</span> Selecciona el medio de pago</h3>
            <div class="pay-methods">
              <button
                type="button"
                class="pay-method"
                :class="{ 'is-selected': pay_method === 'bank' }"
                @click="pay_method = 'bank'"
              >
                <i class="fas fa-university"></i>
                <div>
                  <strong>Transferencia</strong>
                  <small>Realiza una transferencia desde tu banco.</small>
                </div>
              </button>
              <button
                type="button"
                class="pay-method"
                :class="{ 'is-selected': pay_method === 'cash' }"
                @click="pay_method = 'cash'"
              >
                <i class="fas fa-money-bill-wave"></i>
                <div>
                  <strong>Efectivo</strong>
                  <small>Paga en oficina con dinero en efectivo.</small>
                </div>
              </button>
            </div>

            <div class="transfer-form" v-if="pay_method === 'bank'">
              <h4>Registra los datos de tu transferencia</h4>
              <p class="transfer-hint">
                Realiza el pago utilizando los datos de la oficina y registra la información de tu transferencia.
              </p>
              <div class="transfer-grid">
                <label class="field">
                  <span>Monto pagado</span>
                  <input :value="`Bs ${remaining.toFixed(2)}`" readonly />
                </label>
                <label class="field">
                  <span>Entidad financiera</span>
                  <input v-model="bank" list="bank-list" placeholder="Selecciona o escribe la entidad" />
                  <datalist id="bank-list">
                    <option value="Banco Unión" />
                    <option value="Banco Nacional de Bolivia" />
                    <option value="Banco Mercantil Santa Cruz" />
                    <option value="Banco BISA" />
                    <option value="Banco Económico" />
                    <option value="Banco Ganadero" />
                    <option value="Banco FIE" />
                    <option value="Yape" />
                    <option value="QR" />
                  </datalist>
                </label>
                <label class="field">
                  <span>Fecha de pago</span>
                  <input v-model="date" type="date" />
                </label>
                <label class="field">
                  <span>Número de voucher</span>
                  <input
                    v-model="voucher_number"
                    placeholder="Ingresa el número de operación"
                    @input="voucher_number = ($event.target.value || '').replace(/[^0-9]/g, '')"
                  />
                </label>
              </div>

              <label class="upload" :class="{ 'has-file': !!voucher }">
                <input type="file" accept=".png,.jpg,.jpeg,.pdf,image/*" @change="onFileChange" />
                <img v-if="voucher && !isPdf" :src="voucher" alt="Comprobante" />
                <template v-else>
                  <i class="fas fa-cloud-upload-alt"></i>
                  <strong>Adjuntar comprobante de pago</strong>
                  <small>Haz clic para adjuntar el comprobante (PNG, JPG o PDF).</small>
                  <small v-if="file">{{ file.name }}</small>
                </template>
              </label>

              <p class="secure-note">
                <i class="fas fa-lock"></i>
                Tu información de pago será validada por nuestro equipo.
              </p>
            </div>

            <div class="cash-note" v-if="pay_method === 'cash'">
              <p>
                Tu pedido será registrado y podrás realizar el pago en la oficina seleccionada.
                Monto a pagar: <b>Bs {{ remaining.toFixed(2) }}</b>
              </p>
            </div>
          </div>

          <p class="shop-error" v-if="error">{{ error }}</p>
          <p class="shop-success" v-if="success">Activación enviada correctamente</p>

          <div class="checkout-mobile-actions">
            <div class="checkout-mobile-actions__summary">
              <div class="row"><span>Total</span><span>Bs {{ price.toFixed(2) }}</span></div>
              <div class="row"><span>Puntos</span><span>{{ points }} pts</span></div>
            </div>
            <button type="button" class="btn btn--ghost" @click="step = 1" :disabled="sending">
              <i class="fas fa-arrow-left"></i>
              Volver al catálogo
            </button>
            <button type="button" class="btn btn--primary" v-show="!sending" @click="POST">
              Confirmar pago y realizar pedido
            </button>
            <button type="button" class="btn btn--primary" v-show="sending" disabled>
              Enviando orden...
            </button>
          </div>
        </section>

        <aside class="shop-cart">
          <div class="shop-cart__head">
            <h3>
              <i class="fas fa-shopping-cart"></i>
              Carrito de compras
              <span class="shop-cart__badge" v-if="cartCount">{{ cartCount }}</span>
            </h3>
          </div>

          <div class="shop-cart__body" v-if="cartItems.length">
            <article class="cart-item" v-for="item in cartItems" :key="'c-' + (item.id || item.name)">
              <img :src="item.img" :alt="item.name" />
              <div class="cart-item__info">
                <strong>{{ item.name }}</strong>
                <span>Bs {{ item.price }}</span>
                <small>{{ item.points || item.price }} puntos</small>
              </div>
              <button type="button" class="cart-item__remove" @click="removeProduct(item)">
                <i class="fas fa-trash-alt"></i>
              </button>
              <div class="cart-item__qty">
                <button type="button" @click="less(item)">−</button>
                <span>{{ item.total }}</span>
                <button type="button" @click="more(item)">+</button>
              </div>
              <div class="cart-item__line">Bs {{ (item.price * item.total).toFixed(2) }}</div>
            </article>
          </div>

          <div class="shop-cart__summary">
            <h4>Resumen del pedido</h4>
            <div class="row"><span>Total productos</span><span>{{ cartCount }} productos</span></div>
            <div class="row"><span>Subtotal</span><span>Bs {{ subtotal.toFixed(2) }}</span></div>
            <div class="row"><span>Impuestos (18%)</span><span>Bs {{ IGV.toFixed(2) }}</span></div>
            <div class="row row--total"><span>Total</span><span>Bs {{ price.toFixed(2) }}</span></div>
            <div class="row"><span>Puntos totales</span><span>{{ points }}</span></div>
          </div>

          <div class="shop-cart__actions">
            <button type="button" class="btn btn--ghost" @click="step = 1" :disabled="sending">
              <i class="fas fa-arrow-left"></i>
              Volver al catálogo
            </button>
            <button type="button" class="btn btn--primary" v-show="!sending" @click="POST">
              Confirmar pago y realizar pedido
            </button>
            <button type="button" class="btn btn--primary" v-show="sending" disabled>
              Enviando orden...
            </button>
          </div>
        </aside>
      </template>
    </div>
  </App>
</template>

<script>
import App from '@/views/layouts/App'
import api from '@/api'
import lib from '@/lib'

export default {
  components: { App },
  data() {
    return {
      platform_configs: null,
      current_points: null,
      current_profit: null,
      products: [],
      balance: null,
      _balance: null,
      _balance_aff: null,
      check: false,
      voucher: null,
      error: null,
      file: null,
      office: null,
      offices: null,
      loading: true,
      sending: false,
      success: false,
      pay_method: null,
      bank: null,
      date: null,
      voucher_number: null,
      invalid: false,
      showCartModal: false,
      step: 1,
      search: '',
      category: 'Todos',
      defaultQr: 'https://ik.imagekit.io/asu/vitalyfuxion/new_qr_FBSQSPj1i.jpg',
      mobileCartStuck: false,
      mobileCartBarHeight: 0,
      mobileCartStuckStyle: null,
      _mobileCartObserver: null,
    }
  },
  computed: {
    session() {
      return this.$store.state.session
    },
    office_id() {
      return this.$store.state.office_id
    },
    price() {
      if (!this.products.length) return 0
      return this.products.reduce((a, b) => a + b.price * b.total, 0)
    },
    points() {
      if (!this.products.length) return 0
      return this.products.reduce((a, b) => a + (b.points || 0) * b.total, 0)
    },
    total() {
      if (!this.products.length) return 0
      return this.products.reduce((a, b) => a + b.total, 0)
    },
    IGV() {
      if (!this.price) return 0
      return this.price - this.price / 1.18
    },
    subtotal() {
      return this.price - this.IGV
    },
    remaining() {
      if (this.check) {
        const ret = this.price - (this.balance + this._balance + this._balance_aff)
        return ret > 0 ? ret : 0
      }
      return this.price
    },
    categories() {
      const arr = this.products.map((x) => x.type).filter(Boolean)
      return arr.filter((v, i, self) => i === self.indexOf(v))
    },
    filteredProducts() {
      const q = this.search.toLowerCase()
      return this.products.filter((p) => {
        const matchCat = this.category === 'Todos' || p.type === this.category
        const matchSearch = !q || (p.name || '').toLowerCase().includes(q)
        return matchCat && matchSearch
      })
    },
    cartItems() {
      return this.products.filter((p) => p.total > 0)
    },
    cartCount() {
      return this.total
    },
    isPdf() {
      return this.file && /\.pdf$/i.test(this.file.name || '')
    },
  },
  async created() {
    const { data } = await api.Activation.GET(this.session)

    if (data.error) {
      this.invalid = true
      return
    }

    this.loading = false

    if (data.error && data.msg == 'invalid session') this.$router.push('/login')

    this.$store.commit('SET_NAME', data.name)
    this.$store.commit('SET_LAST_NAME', data.lastName)
    this.$store.commit('SET_AFFILIATED', data.affiliated)
    this.$store.commit('SET_ACTIVATED', data.activated)
    this.$store.commit('SET__ACTIVATED', data._activated)
    this.$store.commit('SET_PLAN', data.plan)
    this.$store.commit('SET_COUNTRY', data.country)
    this.$store.commit('SET_PHOTO', data.photo)
    this.$store.commit('SET_TREE', data.tree)

    this.platform_configs = data.platform_configs
    this.current_points = data.points
    this.current_profit = data.profit
    this.products = data.products.map((a) => ({ ...a, total: 0 }))

    this.balance = data.balance
    this._balance = data._balance
    this._balance_aff = data._balance_aff
    this.offices = data.offices

    if (this.office_id && this.offices) {
      const foundOffice = this.offices.find((o) => o.id === this.office_id)
      if (foundOffice) this.office = foundOffice
    }
  },
  mounted() {
    document.body.classList.add('vf-sticky-cart')
    this.$nextTick(() => this.setupStickyMobileCart())
    window.addEventListener('resize', this.setupStickyMobileCart)
  },
  beforeDestroy() {
    document.body.classList.remove('vf-sticky-cart')
    window.removeEventListener('resize', this.setupStickyMobileCart)
    if (this._mobileCartObserver) {
      this._mobileCartObserver.disconnect()
      this._mobileCartObserver = null
    }
  },
  watch: {
    cartCount(n) {
      if (n === 0 && this.step === 2) this.step = 1
    },
    step() {
      this.$nextTick(() => this.setupStickyMobileCart())
    },
    loading(v) {
      if (!v) this.$nextTick(() => this.setupStickyMobileCart())
    },
  },
  methods: {
    setupStickyMobileCart() {
      if (typeof window === 'undefined') return
      this.removeStickyMobileCart()
      this.$nextTick(() => {
        if (window.innerWidth > 1099) return

        const bar = this.$el.querySelector('.mobile-cart-bar')
          ? this.$el.querySelector('.mobile-cart-bar')
          : document.querySelector('.mobile-cart-bar')
        const anchor = document.querySelector('.mobile-cart-anchor')
        const sentinel = this.$refs.mobileCartSentinel || document.querySelector('.mobile-cart-sentinel')
        
        if (!bar || !sentinel || !anchor) {
          setTimeout(() => this.setupStickyMobileCart(), 120)
          return
        }

        const lockMetricsFromNaturalSize = () => {
          const source = this.mobileCartStuck ? anchor : bar;
          const rect = source.getBoundingClientRect();
          if (rect.width > 0) {
            this.mobileCartBarLeft = Math.round(rect.left);
            this.mobileCartBarWidth = Math.round(rect.width);
            this.mobileCartBarHeight = Math.round(rect.height || bar.offsetHeight || 72);
          }
        };

        lockMetricsFromNaturalSize();

        const applyStuck = (nextStuck) => {
          if (nextStuck && !this.mobileCartStuck) {
            lockMetricsFromNaturalSize();
          }
          if (!nextStuck) {
            this.mobileCartStuck = false;
            this.mobileCartStuckStyle = null;
            return;
          }
          this.mobileCartStuck = true;
          this.mobileCartStuckStyle = {
            width: `${this.mobileCartBarWidth}px`,
            left: `${this.mobileCartBarLeft}px`,
          };
        };

        const syncStuck = () => {
          if (this.showCartModal) {
            this.mobileCartStuck = false;
            this.mobileCartStuckStyle = null;
            return;
          }
          const top = sentinel.getBoundingClientRect().top;
          applyStuck(top <= 8);
        };

        if (typeof IntersectionObserver !== 'undefined') {
          this._mobileCartObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry || this.showCartModal) {
                this.mobileCartStuck = false;
                this.mobileCartStuckStyle = null;
                return;
              }
              applyStuck(!entry.isIntersecting && entry.boundingClientRect.top < 8);
            },
            {
              root: null,
              threshold: 0,
              rootMargin: '-8px 0px 0px 0px',
            }
          );
          this._mobileCartObserver.observe(sentinel);
        }

        this._mobileCartOnScroll = syncStuck;
        this._mobileCartScrollTargets = [window];
        const content = document.querySelector('.app > section .content');
        if (content) this._mobileCartScrollTargets.push(content);
        const appEl = document.querySelector('.app');
        if (appEl) this._mobileCartScrollTargets.push(appEl);
        
        this._mobileCartScrollTargets.forEach((target) => {
          target.addEventListener('scroll', syncStuck, { passive: true });
        });
        
        this._mobileCartOnResize = () => {
          lockMetricsFromNaturalSize();
          syncStuck();
        };
        window.addEventListener('resize', this._mobileCartOnResize, { passive: true });
      })
    },
    removeStickyMobileCart() {
      if (this._mobileCartObserver) {
        this._mobileCartObserver.disconnect()
        this._mobileCartObserver = null
      }
      if (this._mobileCartScrollTargets && this._mobileCartOnScroll) {
        this._mobileCartScrollTargets.forEach(target => {
          target.removeEventListener('scroll', this._mobileCartOnScroll)
        })
        this._mobileCartScrollTargets = null
        this._mobileCartOnScroll = null
      }
      if (this._mobileCartOnResize) {
        window.removeEventListener('resize', this._mobileCartOnResize)
        this._mobileCartOnResize = null
      }
    },
    addProduct(product) {
      this.more(product)
    },
    more(product) {
      if (product.total == 10) return
      product.total += 1
    },
    less(product) {
      if (product.total == 0) return
      product.total -= 1
    },
    removeProduct(product) {
      product.total = 0
    },
    goCheckout() {
      if (!this.cartCount) return
      this.error = null
      this.success = false
      this.step = 2
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    scrollToCart() {
      this.showCartModal = true
    },
    async copyAccounts() {
      if (!this.office || !this.office.accounts) return
      try {
        await navigator.clipboard.writeText(this.office.accounts)
      } catch (e) {
        /* ignore */
      }
    },
    onFileChange(e) {
      this.file = e.target.files[0]
      if (!this.file) {
        this.voucher = null
        return
      }
      const reader = new FileReader()
      reader.onload = (ev) => {
        this.voucher = ev.target.result
      }
      reader.readAsDataURL(this.file)
    },
    reset() {
      this.products.forEach((product) => {
        product.total = 0
      })
      this.check = false
      this.pay_method = null
      this.bank = null
      this.date = null
      this.voucher_number = null
      this.voucher = null
      this.file = null
      this.step = 1
    },
    async POST() {
      let {
        products,
        office,
        check,
        voucher,
        pay_method,
        bank,
        date,
        voucher_number,
      } = this

      if (pay_method == 'bank') {
        if (!bank) {
          this.error = 'Nombre de entidad'
          return
        }
        if (!date) {
          this.error = 'Fecha de voucher'
          return
        }
        if (!voucher_number) {
          this.error = 'Número de voucher'
          return
        }
        if (!voucher) {
          this.error = 'Voucher de pago'
          return
        }
      }

      if (!this.total) {
        this.error = 'Seleccione productos'
        return
      }

      if (!office) {
        this.error = 'Seleccione oficina'
        return
      }

      if (!check && !pay_method) {
        this.error = 'Seleccione Medio de Pago'
        return
      }

      if (check && this.remaining && !pay_method) {
        this.error = 'Seleccione Medio de Pago'
        return
      }

      this.error = null
      this.sending = true

      if (voucher) voucher = await lib.upload(this.file, this.file.name, 'activations')

      await api.Activation.POST(this.session, {
        products,
        voucher,
        office: office.id,
        check,
        pay_method,
        bank,
        date,
        voucher_number,
      })

      this.sending = false
      this.success = true
      this.reset()
    },
  },
}
</script>

<style lang="stylus" scoped>
c-gold = #D4AF37
c-gold-dark = #c9a227
c-ink = #1a1a1a
c-muted = #6b6b6b
c-line = #e6e6e6
c-bg = #f5f5f5

.shop-tabs
  display flex
  gap 16px
  margin-bottom 16px
  &__link
    color c-muted
    font-weight 600
    text-decoration none
    padding-bottom 6px
    border-bottom 2px solid transparent
    &.is-active
    &.router-link-exact-active
      color c-ink
      border-bottom-color c-gold

.shop-error
  color #c0392b
  font-weight 600
.shop-success
  color #1e7a3a
  font-weight 600
  margin-top 12px

.shop
  display grid
  grid-template-columns minmax(0, 1fr)
  gap 20px
  align-items start
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box
  @media (min-width 1100px)
    grid-template-columns minmax(0, 1fr) 400px
    gap 20px

.shop-main
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box

.shop-header
  display none
  @media (min-width 1100px)
    display flex
    flex-direction row
    justify-content space-between
    align-items flex-start
    gap 16px
    margin-bottom 20px

.shop-title
  margin 0
  font-size 28px
  font-weight 700
  color c-ink
  padding-left 12px
  border-left 4px solid c-gold

.shop-subtitle
  margin 10px 0 0
  color c-muted
  font-size 14px
  line-height 1.45
  max-width 560px

.shop-stepper
  display flex
  align-items center
  gap 10px
  flex-shrink 0
  &__item
    display flex
    align-items center
    gap 8px
    color #9a9a9a
    font-size 13px
    font-weight 600
    &.is-active
    &.is-done
      color c-ink
      .shop-stepper__num
        background c-gold
        color #111
  &__num
    width 28px
    height 28px
    border-radius 50%
    background #ddd
    display inline-flex
    align-items center
    justify-content center
    font-size 12px
  &__line
    width 36px
    height 2px
    background #ddd
    &.is-done
      background c-gold

.shop-toolbar
  display flex
  flex-direction column
  gap 12px
  margin-bottom 10px
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box
  @media (max-width 1099px)
    background #fff
    border-radius 12px
    border 1px solid #f0f0f0
    box-shadow 0 2px 8px rgba(0,0,0,.05)
    padding 12px
    margin-bottom 12px

.shop-search
  display flex
  align-items center
  gap 10px
  background white
  border 1px solid c-line
  border-radius 999px
  padding 10px 16px
  min-width 0
  width 100%
  box-sizing border-box
  @media (max-width 1099px)
    background #f8f9fa
    border 2px solid #e0e0e0
    padding 12px 16px
  i
    color #666
  input
    border none
    outline none
    width 100%
    min-width 0
    font-size 14px
    background transparent

.shop-filters
  display flex
  gap 8px
  overflow-x auto
  padding-bottom 4px
  -webkit-overflow-scrolling touch
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box
  &::-webkit-scrollbar
    display none

.shop-chip
  border 2px solid #e0e0e0
  background white
  color #666
  border-radius 999px
  padding 8px 16px
  font-size 12px
  font-weight 600
  white-space nowrap
  cursor pointer
  flex-shrink 0
  &.is-active
    background c-gold
    color #111
    border-color c-gold

.shop-count
  text-align center
  font-size 13px
  color #555
  margin 16px 0
  background #f9f9f9
  border 1px solid #eaeaea
  border-radius 8px
  padding 12px
  font-weight 500
  @media (max-width 1099px)
    margin 0 0 12px
    padding 8px 12px
    background #f3f3f3
    border-radius 8px
    font-size 12px

.shop-grid
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 10px
  overflow hidden
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box
  @media (min-width 768px)
    grid-template-columns repeat(4, minmax(0, 1fr))
    gap 14px
    overflow visible

.product-card
  position relative
  min-width 0
  width 100%
  max-width 100%
  box-sizing border-box
  background white
  border-radius 12px
  box-shadow 0 2px 10px rgba(0,0,0,.06)
  border 1px solid #eee
  padding 10px 8px 10px
  display flex
  flex-direction column
  align-items stretch
  &__badge
    display inline-flex
    align-items center
    gap 4px
    position absolute
    top 6px
    right 6px
    z-index 2
    background c-gold
    color #111
    font-size 10px
    font-weight 700
    padding 3px 7px
    border-radius 999px
    i
      font-size 9px
  &__img
    height 80px
    display flex
    align-items center
    justify-content center
    margin-bottom 8px
    img
      max-width 100%
      max-height 80px
      object-fit contain
  &__info
    display flex
    flex-direction column
    flex 1
  &__name
    margin 0 0 4px
    font-size 11px
    font-weight 700
    text-transform uppercase
    color c-gold
    line-height 1.2
    display -webkit-box
    -webkit-line-clamp 2
    -webkit-box-orient vertical
    overflow hidden
  &__type
    font-size 10px
    color c-muted
    margin 0 0 4px
    display block
  &__price
    margin 4px 0 8px
    font-size 13px
    font-weight 700
    color c-ink
  &__price-label
    font-size 10px
    color c-muted
    font-weight 400
    margin-right 3px
  &__btn
    margin-top auto
    background c-gold
    color #111
    border none
    border-radius 8px
    padding 8px 6px
    font-weight 700
    font-size 11px
    cursor pointer
    display flex
    align-items center
    justify-content center
    gap 5px
    i
      font-size 11px
  &__controls
    margin-top auto
    display flex
    align-items center
    justify-content space-between
    background #f5f5f5
    border-radius 8px
    padding 3px
    .btn-ctrl
      width 28px
      height 28px
      border none
      background white
      color c-ink
      border-radius 6px
      font-size 14px
      display flex
      align-items center
      justify-content center
      cursor pointer
      box-shadow 0 1px 4px rgba(0,0,0,.05)
    .ctrl-qty
      font-weight 700
      font-size 13px
      color c-ink
      min-width 24px
      text-align center

@media (min-width 768px)
  .product-card
    position relative
    overflow hidden
    padding 16px 14px 52px
    border-radius 14px
    box-shadow 0 8px 24px rgba(0,0,0,.06)
    min-height 300px
    align-items stretch
    &__badge
      display inline-flex
      align-items center
      gap 4px
      position absolute
      top 10px
      right 10px
      background #D4AF37
      color #fff
      font-size 11px
      font-weight 700
      padding 4px 10px
      border-radius 6px
      box-shadow 0 3px 8px rgba(0,0,0,0.15)
      border 1.5px solid #fff
      z-index 3
      i
        font-size 10px
        color #fff
    &__img
      height 140px
      img
        max-height 140px
    &__info
      text-align center
      align-items center
      width 100%
    &__name
      font-size 14px
      min-height 36px
      color #B38728
      text-align center
      width 100%
    &__type
      display block
      text-align center
      color #666
      font-size 12px
      margin 0 0 6px
    &__price
      display block
      font-size 20px
      margin 2px 0 10px
      color #111
      text-align center
      font-weight 800
    &__price-label
      display block
      text-align center
      color #666
      font-weight 500
    &__btn
      width 100%
      height 44px
      margin 0
      padding 0
      font-size 13px
      border-radius 0 0 14px 14px
      background #D4AF37
      color #fff
      box-shadow none
      i
        color #fff
    &__controls
      position absolute
      left 0
      right 0
      bottom 0
      width 100%
      height 44px
      margin 0
      border-radius 0 0 14px 14px
      padding 0 10px
      background #D4AF37
      box-sizing border-box
      .btn-ctrl
        width 32px
        height 32px
        font-size 16px
        border-radius 8px
        background transparent
        color #fff
        box-shadow none
      .ctrl-qty
        font-size 15px
        color #fff

  .product-catalog-info
    text-align center
    align-items center
  .product-catalog-name
    color #B38728
    text-align center
  .product-catalog-info-text
    display block
    text-align center
    color #666
  .product-catalog-price
    text-align center
    font-size 12px
    color #666
    font-weight 500
    width 100%
    .price-amount
      display block
      color #111
      font-size 20px
      font-weight 800
      margin-top 2px
  .add-to-cart-container
    position absolute
    left 0
    right 0
    bottom 0
    width 100%
    height 44px
    margin 0
  .add-to-cart-btn
    width 100%
    height 44px
    margin 0
    border-radius 0 0 14px 14px
    background #D4AF37
    color #fff
    box-shadow none
    i
      color #fff

.cart-overlay
  position fixed
  top 0
  left 0
  width 100%
  height 100%
  background rgba(0,0,0,0.5)
  z-index 9999
  opacity 0
  pointer-events none
  transition opacity 0.3s ease
  &.is-open
    opacity 1
    pointer-events auto

.shop-cart
  background white
  border-radius 16px
  box-shadow 0 10px 28px rgba(0,0,0,.08)
  padding 18px
  position sticky
  top 12px
  align-self start
  max-height calc(100vh - 24px)
  overflow-y auto
  @media (max-width 1099px)
    display none
  &--modal
    position fixed
    top 0
    right 0
    width 100%
    max-width 100%
    height 100vh
    height 100dvh
    background white
    box-shadow -4px 0 24px rgba(0,0,0,0.1)
    padding 20px 16px
    display flex
    flex-direction column
    transform translateX(100%)
    transition transform 0.3s ease
    border-radius 0
    z-index 10000
    max-height none
    overflow-y auto
    @media (min-width 700px)
      width 85%
      max-width 420px
      padding 24px 20px
    @media (max-width 1099px)
      display flex !important
    .is-open &
      transform translateX(0)
  &__head
    display flex
    flex-direction column
    position relative
    padding-bottom 16px
    border-bottom 1px solid #eee
    h3
      margin 0
      color c-gold
      font-size 22px
      text-align center
      font-weight 700
      display flex
      align-items center
      justify-content center
      gap 8px
    p
      margin 6px 0 0
      color c-muted
      font-size 13px
      text-align center
  &__subtitle
    color c-gold
    font-size 18px
    text-align center
    margin 20px 0 12px
  &__close
    position absolute
    top 0
    right 0
    background transparent
    border none
    font-size 24px
    color #aaa
    cursor pointer
    background c-gold
    color #111
    border-radius 999px
    font-size 11px
    min-width 20px
    height 20px
    display inline-flex
    align-items center
    justify-content center
    padding 0 6px
  &__body
    margin-top 14px
    max-height 280px
    overflow auto
    display flex
    flex-direction column
    gap 12px
  &__empty
    margin 28px 0
    text-align center
    color #b0b0b0
    i
      font-size 42px
      margin-bottom 10px
    p
      margin 0
      font-weight 600
      color #888
    small
      color #aaa
  &__summary
    margin-top 16px
    border-top 1px solid c-line
    padding-top 14px
    h4
      margin 0 0 10px
      font-size 14px
    .row
      display flex
      justify-content space-between
      font-size 13px
      color #555
      margin-bottom 8px
      &--total
        font-size 18px
        font-weight 700
        color c-ink
        span:last-child
          color c-gold
  &__actions
    display flex
    flex-direction column
    gap 10px
    margin-top 14px

.cart-item
  display flex
  align-items flex-start
  padding 12px
  border 1px solid #f0f0f0
  border-radius 12px
  margin-bottom 12px
  background white
  img
    width 64px
    height 64px
    object-fit contain
    border-radius 8px
    background c-bg
  &__info
    display flex
    flex-direction column
    margin-left 12px
    flex 1
    strong
      font-size 13px
      color c-ink
      text-transform uppercase
    span
      color c-gold
      font-weight 700
      font-size 14px
      margin-top 4px
    small
      color c-muted
      font-weight 700
      font-size 12px
  &__actions
    display flex
    flex-direction column
    align-items flex-end
    gap 8px
  &__qty
    display flex
    align-items center
    gap 6px
    background transparent
    padding 0
    button
      width 26px
      height 26px
      background white
      color c-ink
      border 1px solid #ddd
      border-radius 6px
      font-size 16px
      display flex
      align-items center
      justify-content center
      cursor pointer
      &:hover
        background #f9f9f9
    span
      width 26px
      height 26px
      display flex
      align-items center
      justify-content center
      border 1px solid #ddd
      border-radius 6px
      font-weight 700
      font-size 14px
      color c-ink
  &__remove
    width 26px
    height 26px
    background #c0392b
    color white
    border none
    border-radius 6px
    display flex
    align-items center
    justify-content center
    cursor pointer
    font-size 12px

.btn
  border none
  border-radius 12px
  padding 12px 14px
  font-weight 700
  cursor pointer
  display flex
  align-items center
  justify-content center
  gap 8px
  width 100%
  &--primary
    background c-gold
    color #111
    &:disabled
      background #e5e5e5
      color #999
      cursor not-allowed
  &--ghost
    background #111
    color c-gold
    border 1px solid c-gold

.checkout-mobile-actions
  display none
  @media (max-width 1099px)
    display flex
    flex-direction column
    gap 10px
    background white
    border-radius 14px
    padding 16px
    margin 0 0 88px
    box-shadow 0 6px 18px rgba(0,0,0,.06)
  &__summary
    margin-bottom 4px
    .row
      display flex
      justify-content space-between
      font-size 14px
      color #555
      margin-bottom 6px
      &:last-child
        font-weight 800
        color #111
        font-size 16px
        span:last-child
          color c-gold

.checkout-block
  background white
  border-radius 14px
  padding 18px
  margin-bottom 16px
  box-shadow 0 6px 18px rgba(0,0,0,.04)
  &__title
    margin 0 0 14px
    font-size 16px
    display flex
    align-items center
    gap 10px
    span
      width 24px
      height 24px
      border-radius 50%
      background c-gold
      color #111
      font-size 12px
      display inline-flex
      align-items center
      justify-content center

.field
  display flex
  flex-direction column
  gap 6px
  font-size 13px
  color #444
  span
    font-weight 600
  input, select
    border 1px solid c-line
    border-radius 10px
    padding 10px 12px
    font-size 14px
    background white

.field-hint
  margin 8px 0 0
  color c-muted
  font-size 12px

.checkout-payinfo
  display grid
  grid-template-columns 1fr
  gap 14px
  margin-bottom 16px
  @media (min-width 800px)
    grid-template-columns 1fr 1fr

.pay-card
  background white
  border-radius 14px
  padding 16px
  box-shadow 0 6px 18px rgba(0,0,0,.04)
  h4
    margin 0 0 12px
  &__accounts
    margin 0
    white-space pre-wrap
    font-family inherit
    font-size 13px
    line-height 1.5
    color #333
    background c-bg
    border-radius 10px
    padding 12px
  img
    width 100%
    max-width 220px
    display block
    margin 0 auto
    border-radius 8px

.btn-copy
  margin-top 10px
  border 1px solid c-line
  background white
  border-radius 8px
  padding 8px 12px
  cursor pointer
  font-size 12px
  color #444

.toggle-row
  display flex
  align-items center
  justify-content space-between
  gap 12px
  font-weight 600
  position relative
  .toggle-input
    position absolute
    opacity 0
    width 48px
    height 28px
    right 0
    cursor pointer
    z-index 2
  .toggle-ui
    width 48px
    height 28px
    border-radius 999px
    background #ddd
    position relative
    flex-shrink 0
    transition .2s
    &:after
      content ''
      position absolute
      width 22px
      height 22px
      border-radius 50%
      background white
      top 3px
      left 3px
      transition .2s
  .toggle-input:checked + .toggle-ui
    background c-gold
    &:after
      left 23px

.balance-box
  margin-top 12px
  background c-bg
  border-radius 10px
  padding 12px
  font-size 13px
  p
    margin 0 0 6px
  .ok
    color #1e7a3a
    font-weight 600

.pay-methods
  display grid
  grid-template-columns 1fr
  gap 12px
  @media (min-width 700px)
    grid-template-columns 1fr 1fr

.pay-method
  display flex
  gap 12px
  align-items flex-start
  text-align left
  border 1px solid c-line
  background white
  border-radius 12px
  padding 14px
  cursor pointer
  i
    font-size 20px
    color c-gold
    margin-top 2px
  strong
    display block
    margin-bottom 4px
  small
    color c-muted
    font-size 12px
  &.is-selected
    border-color c-gold
    box-shadow 0 0 0 2px rgba(212,175,55,.25)

.transfer-form
  margin-top 16px
  h4
    margin 0 0 6px
  .transfer-hint
    color c-muted
    font-size 13px
    margin 0 0 14px

.transfer-grid
  display grid
  grid-template-columns 1fr
  gap 12px
  @media (min-width 700px)
    grid-template-columns 1fr 1fr

.upload
  margin-top 14px
  border 1.5px dashed #cfcfcf
  border-radius 12px
  padding 28px 16px
  text-align center
  cursor pointer
  display block
  color c-muted
  position relative
  input
    position absolute
    inset 0
    opacity 0
    cursor pointer
  i
    font-size 28px
    color c-gold
    display block
    margin-bottom 8px
  strong
    display block
    color c-ink
    margin-bottom 4px
  img
    max-width 180px
    max-height 140px
    border-radius 8px
  &.has-file
    border-style solid
    border-color c-gold

.secure-note
  margin 12px 0 0
  color c-muted
  font-size 12px
  i
    color c-gold
    margin-right 6px

.cash-note
  margin-top 14px
  background c-bg
  border-radius 10px
  padding 14px
  font-size 14px
  line-height 1.45
</style>
<style lang="stylus">
.cart-button-mobile-anchor,
.mobile-cart-anchor {
  width: 100%;
  margin: 12px 0 16px;
  position: relative;
}

@media (min-width: 1100px) {
  .cart-button-mobile-anchor,
  .mobile-cart-anchor,
  .cart-button-container-mobile,
  .mobile-cart-bar {
    display: none !important;
  }
}

.cart-button-mobile-sentinel,
.mobile-cart-sentinel {
  width: 100%;
  height: 1px;
  margin: 0;
  padding: 0;
  pointer-events: none;
}

.cart-button-container-mobile,
.mobile-cart-bar {
  display: flex !important;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 16px;
  padding: 14px 18px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
  border: 1px solid #eaeaea;
  position: relative;
  z-index: 40;
  box-sizing: border-box;
  width: 100%;
}

.cart-button-container-mobile.is-stuck,
.mobile-cart-bar.is-stuck {
  position: fixed !important;
  top: 8px !important;
  z-index: 2000 !important;
  margin: 0 !important;
  box-shadow: 0 6px 18px rgba(212, 175, 55, 0.28) !important;
}

.cart-button-container-mobile.is-hidden,
.mobile-cart-bar.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.cart-info-left,
.mobile-cart-bar__info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.cart-price-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.total-price,
.mobile-cart-bar__amount,
.total-items,
.mobile-cart-bar__points {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111;
  line-height: 1.25;
}

.cart-square-btn,
.mobile-cart-bar__btn {
  flex-shrink: 0;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  background: #D4AF37 !important;
  color: #fff !important;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(212, 175, 55, 0.35);
  transition: all 0.25s ease;
}

.cart-square-btn:hover,
.mobile-cart-bar__btn:hover {
  background: #c9a227 !important;
  box-shadow: 0 6px 18px rgba(201, 162, 39, 0.45);
}

.cart-square-btn i,
.mobile-cart-bar__btn i {
  font-size: 1.05rem;
  color: #fff !important;
}

/* Indicador contador como en Sifrah */
.products-count-indicator,
.shop-count {
  grid-column: 1 / -1;
  text-align: center;
  padding: 10px 14px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 0 0 14px;
  font-size: 0.85rem;
  color: #666;
  border: 1px solid #e9ecef;
  width: 100%;
  box-sizing: border-box;
}

.products-count-indicator .filter-active,
.shop-count .filter-active {
  color: #D4AF37;
  font-weight: 600;
  margin-left: 8px;
}

/* Esquina dorada y badge de puntos en Web y Móvil */
.card-corner {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 0 !important;
  height: 0 !important;
  border-style: solid !important;
  border-width: 24px 24px 0 0 !important;
  border-color: #D4AF37 transparent transparent transparent !important;
  z-index: 2 !important;
  opacity: 0.95 !important;
}

/* Badge de puntos en Web y Móvil */
.points-badge,
.product-card__badge {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  position: absolute !important;
  top: 10px !important;
  right: 10px !important;
  background: #D4AF37 !important;
  color: #fff !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  padding: 4px 10px !important;
  border-radius: 6px !important;
  box-shadow: 0 3px 8px rgba(0,0,0,0.15) !important;
  border: 1.5px solid #fff !important;
  z-index: 3 !important;
}

.points-badge i,
.product-card__badge i {
  font-size: 10px !important;
  color: #fff !important;
}

/* Grilla y tarjetas Sifrah */
@media (max-width: 1099px) {
  .shop {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }

  .shop-main {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .shop-toolbar,
  .shop-filters {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .products-catalog-grid,
  .shop-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 10px !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  .product-catalog-card,
  .product-card {
    background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.05) !important;
    padding: 10px 6px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    position: relative !important;
    transition: all 0.3s ease !important;
    border: 1px solid #f0f0f0 !important;
    min-height: 240px !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Doble marco interior como Sifrah */
  .product-catalog-card::before,
  .product-card::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    pointer-events: none;
    z-index: 1;
  }

  .product-catalog-card::after,
  .product-card::after {
    content: '';
    position: absolute;
    top: 5px;
    left: 5px;
    right: 5px;
    bottom: 5px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    pointer-events: none;
    z-index: 1;
  }

  /* Esquina doblada triangular */
  .product-catalog-card .card-corner,
  .product-card .card-corner {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 22px 22px 0 0;
    border-color: #D4AF37 transparent transparent transparent;
    z-index: 2;
    opacity: 0.95;
  }

  /* Badge de puntos estrella */
  .points-badge,
  .product-card__badge {
    position: absolute;
    top: 6px;
    right: 6px;
    background: #D4AF37 !important;
    color: #fff !important;
    border-radius: 4px !important;
    padding: 3px 6px !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    display: flex !important;
    align-items: center !important;
    gap: 3px !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.15) !important;
    z-index: 3 !important;
    border: 1px solid #fff !important;
  }

  .points-badge i,
  .product-card__badge i {
    font-size: 0.6rem !important;
    color: #fff !important;
  }

  /* Contenedor imagen producto centrado */
  .product-image-container,
  .product-card__img {
    width: 84px !important;
    height: 84px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 8px auto 10px auto !important;
    border-radius: 10px !important;
    border: 1px solid #e8e8e8 !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
    background: #fff !important;
    overflow: hidden !important;
    position: relative !important;
    z-index: 2 !important;
    flex-shrink: 0 !important;
  }

  .product-catalog-img,
  .product-card__img img {
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
  }

  /* Textos centrados */
  .product-catalog-info,
  .product-card__info {
    width: 100% !important;
    text-align: center !important;
    margin-bottom: 6px !important;
    position: relative !important;
    z-index: 2 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    flex: 1 !important;
  }

  .product-catalog-name,
  .product-card__name {
    font-size: 0.85rem !important;
    font-weight: 700 !important;
    color: #B38728 !important;
    margin-bottom: 3px !important;
    line-height: 1.2 !important;
    text-align: center !important;
    min-height: 2.2em !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-transform: uppercase !important;
    width: 100% !important;
  }

  .product-catalog-info-text,
  .product-card__type {
    font-size: 0.75rem !important;
    color: #666 !important;
    font-weight: 500 !important;
    margin-bottom: 4px !important;
    line-height: 1.2 !important;
    text-align: center !important;
    min-height: 1.2em !important;
  }

  .product-catalog-price {
    font-size: 0.75rem !important;
    color: #333 !important;
    font-weight: 500 !important;
    margin-bottom: 6px !important;
    text-align: center !important;
  }

  .price-amount,
  .product-card__price {
    display: block !important;
    font-size: 1.15rem !important;
    font-weight: 800 !important;
    color: #111 !important;
    margin-top: 1px !important;
    margin-bottom: 6px !important;
    text-align: center !important;
  }

  .add-to-cart-container {
    margin-top: auto !important;
    width: 100% !important;
    position: relative !important;
    z-index: 2 !important;
  }

  .add-to-cart-btn,
  .product-card__btn {
    background: #D4AF37 !important;
    color: #fff !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 9px 12px !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 6px !important;
    box-shadow: 0 3px 10px rgba(212, 175, 55, 0.3) !important;
    width: 100% !important;
  }

  .add-to-cart-btn i,
  .product-card__btn i {
    font-size: 0.85rem !important;
    color: #fff !important;
  }

  .product-quantity-controls,
  .product-card__controls {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    margin-top: auto !important;
    width: 100% !important;
    background: #D4AF37 !important;
    border-radius: 20px !important;
    padding: 2px !important;
    box-shadow: 0 2px 8px rgba(212, 175, 55, 0.25) !important;
    position: relative !important;
    z-index: 2 !important;
  }

  .qty-control-btn,
  .product-card__controls .btn-ctrl {
    background: transparent !important;
    color: #fff !important;
    border: none !important;
    width: 28px !important;
    height: 28px !important;
    font-size: 1rem !important;
    font-weight: 700 !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: none !important;
  }

  .quantity-display,
  .product-card__controls .ctrl-qty {
    font-size: 0.9rem !important;
    font-weight: 700 !important;
    color: #fff !important;
    min-width: 20px !important;
    text-align: center !important;
  }
}

@media (max-width: 480px) {
  .products-catalog-grid,
  .shop-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
  }

  .product-catalog-card,
  .product-card {
    padding: 10px 6px !important;
    min-height: 235px !important;
  }

  .product-image-container,
  .product-card__img {
    width: 72px !important;
    height: 72px !important;
  }

  .product-catalog-name,
  .product-card__name {
    font-size: 0.8rem !important;
  }
}

/* Desktop: Agregar y cantidad, mismo alto y de extremo a extremo */
@media (min-width: 768px) {
  .product-catalog-card.product-card,
  .product-card {
    padding: 16px 14px 52px !important;
    overflow: hidden !important;
  }

  .add-to-cart-container {
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 44px !important;
    margin: 0 !important;
    z-index: 2 !important;
  }

  .add-to-cart-btn,
  .product-card__btn {
    width: 100% !important;
    height: 44px !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 0 0 14px 14px !important;
    background: #D4AF37 !important;
    color: #fff !important;
    box-shadow: none !important;
  }

  .add-to-cart-btn i,
  .product-card__btn i {
    color: #fff !important;
  }

  .product-quantity-controls,
  .product-card__controls {
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 44px !important;
    margin: 0 !important;
    padding: 0 10px !important;
    border-radius: 0 0 14px 14px !important;
    background: #D4AF37 !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
    z-index: 2 !important;
  }

  .product-card__controls .btn-ctrl,
  .qty-control-btn {
    width: 32px !important;
    height: 32px !important;
    background: transparent !important;
    color: #fff !important;
    box-shadow: none !important;
  }

  .product-card__controls .ctrl-qty,
  .quantity-display {
    color: #fff !important;
    font-size: 15px !important;
  }
}

/* Permite sticky del carrito desktop (el layout app usa overflow:hidden) */
@media (min-width: 1100px) {
  body.vf-sticky-cart .app > section {
    overflow: visible !important;
  }
  body.vf-sticky-cart .app > section .content {
    overflow: visible !important;
  }
  body.vf-sticky-cart .app > section .content > section {
    overflow: visible !important;
  }
}
</style>
